import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Folder, Edit2, Trash2, Users, FileText, X, CheckCircle, BarChart3, Settings as SettingsIcon, Upload, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import ProjectIcon from '../components/ProjectIcon';
import HelperNote from '../components/HelperNote';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Projects() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [settingsProject, setSettingsProject] = useState(null);
  const [settingsMaxFileSize, setSettingsMaxFileSize] = useState(50);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [taskCounts, setTaskCounts] = useState({});
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [formData, setFormData] = useState({ 
    name: '', 
    description: ''
  });
  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);          // File object pending upload (create mode)
  const [logoPreview, setLogoPreview] = useState(null);    // blob: URL for preview
  const [logoUploading, setLogoUploading] = useState(false);
  const createLogoInputRef = useRef(null);
  const editLogoInputRef = useRef(null);

  // Reset form to initial state
  const resetProjectForm = () => {
    setFormData({
      name: '',
      description: ''
    });
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
  };

  const handleLogoFilePick = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir resim dosyası seçin');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo en fazla 3 MB olabilir');
      return;
    }
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  // Upload logo to a specific project (used by edit modal — instant upload).
  const uploadLogoToProject = async (projectId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/projects/${projectId}/logo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const handleEditLogoPick = async (file) => {
    if (!file || !projectToEdit) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir resim dosyası seçin');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo en fazla 3 MB olabilir');
      return;
    }
    setLogoUploading(true);
    try {
      const res = await uploadLogoToProject(projectToEdit.id, file);
      // Update local edit state and refresh list
      setProjectToEdit({ ...projectToEdit, logo_url: res.data.logo_url });
      toast.success('Logo yüklendi');
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo yüklenemedi');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveProjectLogo = async () => {
    if (!projectToEdit) return;
    setLogoUploading(true);
    try {
      await axios.delete(`${API}/projects/${projectToEdit.id}/logo`);
      setProjectToEdit({ ...projectToEdit, logo_url: null });
      toast.success('Logo kaldırıldı');
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo silinemedi');
    } finally {
      setLogoUploading(false);
    }
  };

  useEffect(() => {
    // ⚡ PERFORMANCE: Paralel API çağrıları + Timing
    const pageLoadStart = performance.now();
    console.log('⏱️ [PROJECTS] Sayfa yükleme başladı');
    
    Promise.all([
      fetchProjects(),
      fetchTeams(),
      fetchUsers(),
      fetchTaskCounts()
    ]).then(() => {
      const totalTime = performance.now() - pageLoadStart;
      console.log(`✅ [PROJECTS] Tüm veriler yüklendi - Toplam süre: ${totalTime.toFixed(0)}ms`);
    }).catch(error => {
      const totalTime = performance.now() - pageLoadStart;
      console.error(`❌ [PROJECTS] Veri yükleme hatası (${totalTime.toFixed(0)}ms):`, error);
    });
  }, []);

  const fetchProjects = async () => {
    const start = performance.now();
    try {
      console.log('📡 [PROJECTS] API çağrısı başladı: /projects');
      const response = await axios.get(`${API}/projects`);
      const elapsed = performance.now() - start;
      console.log(`✅ [PROJECTS] API yanıtı alındı: ${elapsed.toFixed(0)}ms, ${response.data.length} proje`);
      const sorted = [...response.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setProjects(sorted);
      const renderTime = performance.now() - start;
      console.log(`🎨 [PROJECTS] Render tamamlandı: ${renderTime.toFixed(0)}ms`);
    } catch (error) {
      const elapsed = performance.now() - start;
      console.error(`❌ [PROJECTS] API hatası (${elapsed.toFixed(0)}ms):`, error);
      toast.error('Projeler yüklenemedi');
    }
  };

  const fetchTeams = async () => {
    const start = performance.now();
    try {
      console.log('📡 [TEAMS] API çağrısı başladı: /teams');
      const response = await axios.get(`${API}/teams`);
      const elapsed = performance.now() - start;
      console.log(`✅ [TEAMS] API yanıtı: ${elapsed.toFixed(0)}ms`);
      setTeams(response.data);
    } catch (error) {
      const elapsed = performance.now() - start;
      console.error(`❌ [TEAMS] API hatası (${elapsed.toFixed(0)}ms):`, error);
    }
  };

  const fetchUsers = async () => {
    const start = performance.now();
    try {
      console.log('📡 [USERS] API çağrısı başladı: /users');
      const response = await axios.get(`${API}/users`);
      const elapsed = performance.now() - start;
      console.log(`✅ [USERS] API yanıtı: ${elapsed.toFixed(0)}ms`);
      setUsers(response.data);
    } catch (error) {
      const elapsed = performance.now() - start;
      console.error(`❌ [USERS] API hatası (${elapsed.toFixed(0)}ms):`, error);
    }
  };

  const fetchTaskCounts = async () => {
    try {
      const response = await axios.get(`${API}/projects/task-counts`);
      setTaskCounts(response.data || {});
    } catch (e) {
      console.error('[TASK-COUNTS]', e);
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Bilinmeyen Takım';
  };

  const getTeamMemberCount = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (!team || !team.memberIds) return 0;
    
    // Sadece gerçekten var olan (aktif) üyeleri say
    const activeMemberCount = team.memberIds.filter(memberId => {
      const user = users.find(u => u.id === memberId);
      return user && user.status === 'active';
    }).length;
    
    return activeMemberCount;
  };

  const getUserById = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser ? foundUser.name : 'Bilinmeyen Kullanıcı';
  };

  // Kullanıcı objesini döndür (resim vb. için)
  const getUserObjectById = (userId) => {
    return users.find(u => u.id === userId) || null;
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/projects`, formData);
      const created = res.data;
      // Eğer logo seçildiyse, hemen yükle
      if (logoFile && created?.id) {
        try {
          await uploadLogoToProject(created.id, logoFile);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Logo yüklenemedi');
        }
      }
      toast.success('Proje oluşturuldu');
      setShowCreateDialog(false);
      resetProjectForm();
      
      // ✅ PWA için hard reload - cache bypass
      setTimeout(() => {
        window.location.href = window.location.href.split('?')[0] + '?_refresh=' + Date.now();
      }, 800);
    } catch (error) {
      console.error('Proje oluşturma hatası:', error.response?.data);
      toast.error('Proje oluşturulamadı: ' + (error.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const openEditDialog = (project) => {
    setProjectToEdit(project);
    setFormData({
      name: project.name,
      description: project.description || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    
    const canDirectEdit = user?.permissions?.includes('can_manage_projects');
    
    if (canDirectEdit) {
      try {
        await axios.patch(`${API}/projects/${projectToEdit.id}`, formData);
        toast.success('Proje güncellendi');
        setShowEditDialog(false);
        setProjectToEdit(null);
        setFormData({ name: '', description: '' });
        fetchProjects();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
      }
    } else {
      toast.error('Bu projeyi düzenleme yetkiniz yok');
      setShowEditDialog(false);
      setProjectToEdit(null);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    
    const canDirectDelete = user?.permissions?.includes('can_manage_projects');
    
    if (canDirectDelete) {
      try {
        await axios.delete(`${API}/projects/${projectToDelete.id}`);
        toast.success('Proje silindi');
        setShowDeleteDialog(false);
        setProjectToDelete(null);
        
        // ✅ PWA için hard reload - cache bypass
        setTimeout(() => {
          window.location.href = window.location.href.split('?')[0] + '?_refresh=' + Date.now();
        }, 800);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Silme başarısız');
      }
    } else {
      toast.error('Bu projeyi silme yetkiniz yok');
      setShowDeleteDialog(false);
      setProjectToDelete(null);
    }
  };

  const canManageProjects = user?.permissions?.includes('can_manage_projects');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <Folder className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="relative">
              Projeler
              {/* Proje sayısı badge */}
              <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
                {projects.length.toLocaleString('tr-TR')}
              </span>
            </span>
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Projelerinizi ve görevlerinizi organize edin
            {projects.length > 0 && (
              <span className="text-blue-600 font-medium text-xs sm:text-sm"> • {projects.length.toLocaleString('tr-TR')} proje</span>
            )}
          </p>
        </div>
        
        {canManageProjects && (
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) resetProjectForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 shadow-lg" 
                data-testid="create-project-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Proje
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              {/* Close Button */}
              <button
                onClick={() => setShowCreateDialog(false)}
                className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
              >
                <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
              </button>
              
              <DialogHeader className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700 -m-6 mb-0 px-6 py-5 rounded-t-lg">
                <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Plus className="w-6 h-6 text-white" />
                  </div>
                  Yeni Proje Oluştur
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleCreateProject} className="space-y-6 py-6 px-1">
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-5 rounded-xl border-2 border-cyan-200">
                  <Label htmlFor="name" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <Folder className="w-5 h-5 text-cyan-600" />
                    Proje Adı <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Örn: Sosyal Medya Kampanyası 2024"
                    required
                    className="bg-white border-2 border-cyan-300 focus:border-cyan-500 focus:ring-cyan-500"
                    data-testid="project-name-input"
                  />
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-200">
                  <Label htmlFor="description" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-purple-600" />
                    Açıklama
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Projenin amacını ve hedeflerini kısaca açıklayın..."
                    rows={4}
                    className="bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                    data-testid="project-description-input"
                  />
                </div>

                {/* Logo / Avatar Upload */}
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border-2 border-amber-200">
                  <Label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <ImageIcon className="w-5 h-5 text-amber-600" />
                    Logo / Avatar (opsiyonel)
                  </Label>
                  <div className="flex items-center gap-4">
                    {/* Preview */}
                    <ProjectIcon
                      project={{
                        id: 'preview-create',
                        name: formData.name || 'Yeni Proje',
                        logo_url: logoPreview,
                      }}
                      size="xl"
                    />
                    <div className="flex-1 flex flex-col gap-2">
                      <input
                        ref={createLogoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="create-logo-input"
                        onChange={(e) => handleLogoFilePick(e.target.files?.[0])}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => createLogoInputRef.current?.click()}
                        className="border-2 border-amber-300 hover:bg-amber-100 text-amber-800"
                        data-testid="create-logo-pick-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {logoFile ? 'Logoyu Değiştir' : 'Logo Yükle'}
                      </Button>
                      {logoFile && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            if (logoPreview) URL.revokeObjectURL(logoPreview);
                            setLogoFile(null);
                            setLogoPreview(null);
                            if (createLogoInputRef.current) createLogoInputRef.current.value = '';
                          }}
                          className="text-red-600 hover:bg-red-50 justify-start"
                          data-testid="create-logo-clear-btn"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Seçimi Kaldır
                        </Button>
                      )}
                      <HelperNote variant="info" className="mt-1">
                        PNG, JPG, WEBP veya SVG • En fazla 3 MB
                      </HelperNote>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg border-t-2 border-gray-200 flex gap-3">
                  <Button 
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => setShowCreateDialog(false)}
                    className="flex-1 border-2 hover:bg-gray-100"
                  >
                    <X className="w-4 h-4 mr-2" />
                    İptal
                  </Button>
                  <Button 
                    type="submit"
                    size="lg"
                    className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold shadow-lg" 
                    data-testid="submit-project-button"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Proje Oluştur
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Folder className="w-12 h-12 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Henüz proje yok</h3>
          <p className="text-gray-600 mb-6">Çalışmaya başlamak için ilk projenizi oluşturun</p>
          {canManageProjects && (
            <Button 
              onClick={() => {
                resetProjectForm();
                setShowCreateDialog(true);
              }} 
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              İlk Projeyi Oluştur
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all border border-gray-100 hover:border-blue-200"
              data-testid={`project-card-${project.id}`}
            >
              <div className="block p-6">
                {/* Card Header with Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-3">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <ProjectIcon project={project} size="lg" className="group-hover:scale-110 transition-transform" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg text-gray-900 mb-1 line-clamp-2 group-hover:text-blue-600 transition-colors">
                        {project.name}
                      </h3>
                      {project.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Icons - Top Right */}
                  {(() => {
                    const isAdmin = user?.role === 'admin';
                    const isProjectManager = (project.managerIds || []).includes(user?.id);
                    const showSummaryButton = isAdmin || isProjectManager;
                    return (
                      <div className="flex gap-1 sm:ml-2 justify-end sm:justify-start">
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSettingsProject(project);
                              setSettingsMaxFileSize(project.max_file_size_mb || 50);
                              setShowSettingsDialog(true);
                            }}
                            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                            data-testid={`settings-project-${project.id}`}
                            title="Proje Ayarları"
                          >
                            <SettingsIcon className="w-4 h-4" />
                          </Button>
                        )}
                        {showSummaryButton && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/projects/${project.id}`);
                            }}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            data-testid={`summary-project-${project.id}`}
                            title="Proje Özeti"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                        )}
                        {isAdmin && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openEditDialog(project);
                              }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`edit-project-${project.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setProjectToDelete(project);
                                setShowDeleteDialog(true);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-project-${project.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Project Info */}
                <div className="pt-4 border-t border-gray-100 space-y-4">
                  {/* Task Stats - Responsive Grid (4x1 desktop, 2x2 tablet, 1x4 mobile) */}
                  {(() => {
                    const c = taskCounts[project.id] || { total: 0, in_progress: 0, in_review: 0, completed: 0 };
                    return (
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        <StatBox label="Toplam" value={c.total} gradient="from-cyan-500 to-blue-500" />
                        <StatBox label="İşlemde" value={c.in_progress} gradient="from-blue-500 to-indigo-500" />
                        <StatBox label="İncelemede" value={c.in_review} gradient="from-amber-500 to-orange-500" />
                        <StatBox label="Tamamlandı" value={c.completed} gradient="from-emerald-500 to-teal-500" />
                      </div>
                    );
                  })()}

                  {/* Project Managers */}
                  {project.managerIds && project.managerIds.length > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-900">
                          Proje Yönetici{project.managerIds.length > 1 ? 'leri' : 'si'} ({project.managerIds.length})
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {project.managerIds.map((managerId) => {
                          const manager = getUserObjectById(managerId);
                          if (!manager) return null;
                          return (
                            <div key={managerId} className="flex items-center gap-2 text-xs">
                              {manager.picture ? (
                                <img src={manager.picture} alt={manager.name} className="w-6 h-6 rounded-full flex-shrink-0" />
                              ) : (
                                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                                  {manager.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <span className="text-purple-900 font-medium">{manager.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Teams - Bağlı Takımlar */}
                  {project.teamIds && project.teamIds.length > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-3 border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-900">
                          Bağlı Takım{project.teamIds.length > 1 ? 'lar' : ''} ({project.teamIds.length})
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {(expandedTeams[project.id] ? project.teamIds : project.teamIds.slice(0, 3)).map((teamId) => (
                          <div key={teamId} className="flex items-center gap-2 text-xs">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></span>
                            <span className="text-blue-900 font-medium truncate">{getTeamName(teamId)}</span>
                            <span className="text-blue-700/70">({getTeamMemberCount(teamId)} üye)</span>
                          </div>
                        ))}
                        {project.teamIds.length > 3 && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setExpandedTeams(prev => ({...prev, [project.id]: !prev[project.id]}));
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 ml-3 cursor-pointer font-medium"
                          >
                            {expandedTeams[project.id] ? 'Daha az göster' : `+${project.teamIds.length - 3} takım daha`}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Close Button */}
          <button
            onClick={() => setShowEditDialog(false)}
            className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
          >
            <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
          </button>
          
          <DialogHeader className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 -m-6 mb-0 px-6 py-5 rounded-t-lg">
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Edit2 className="w-6 h-6 text-white" />
              </div>
              Projeyi Düzenle
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateProject} className="space-y-6 py-6 px-1">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border-2 border-blue-200">
              <Label htmlFor="edit-name" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <Folder className="w-5 h-5 text-blue-600" />
                Proje Adı <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-white border-2 border-blue-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-200">
              <Label htmlFor="edit-description" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-purple-600" />
                Açıklama
              </Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-500"
              />
            </div>

            {/* Logo / Avatar Upload (Edit) */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border-2 border-amber-200">
              <Label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <ImageIcon className="w-5 h-5 text-amber-600" />
                Logo / Avatar
              </Label>
              <div className="flex items-center gap-4">
                <ProjectIcon project={projectToEdit || {}} size="xl" />
                <div className="flex-1 flex flex-col gap-2">
                  <input
                    ref={editLogoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="edit-logo-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleEditLogoPick(f);
                      if (editLogoInputRef.current) editLogoInputRef.current.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={logoUploading}
                    onClick={() => editLogoInputRef.current?.click()}
                    className="border-2 border-amber-300 hover:bg-amber-100 text-amber-800"
                    data-testid="edit-logo-pick-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {logoUploading ? 'Yükleniyor...' : (projectToEdit?.logo_url ? 'Logoyu Değiştir' : 'Logo Yükle')}
                  </Button>
                  {projectToEdit?.logo_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={logoUploading}
                      onClick={handleRemoveProjectLogo}
                      className="text-red-600 hover:bg-red-50 justify-start"
                      data-testid="edit-logo-remove-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Logoyu Kaldır
                    </Button>
                  )}
                  <HelperNote variant="info" className="mt-1">
                    PNG, JPG, WEBP veya SVG • En fazla 3 MB. Logo kaldırılırsa proje rengi ve baş harfler tekrar görünür.
                  </HelperNote>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg border-t-2 border-gray-200 flex gap-3">
              <Button 
                type="button" 
                variant="outline"
                size="lg"
                onClick={() => {
                  setShowEditDialog(false);
                  setProjectToEdit(null);
                  setFormData({ name: '', description: '' });
                }}
                className="flex-1 border-2 hover:bg-gray-100"
              >
                <X className="w-4 h-4 mr-2" />
                İptal
              </Button>
              <Button 
                type="submit"
                size="lg"
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-600">Projeyi Sil</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-gray-900 mb-2">
                <strong className="text-red-600">{projectToDelete?.name}</strong> projesini silmek istediğinizden emin misiniz?
              </p>
              <p className="text-sm text-red-700 flex items-start gap-2">
                <span className="text-lg">⚠️</span>
                <span>Bu işlem geri alınamaz. Projeye ait tüm veriler kalıcı olarak silinecektir.</span>
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setProjectToDelete(null);
                }}
                className="flex-1"
              >
                İptal
              </Button>
              <Button 
                onClick={handleDeleteProject} 
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Evet, Sil
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={(open) => {
        setShowSettingsDialog(open);
        if (!open) { setSettingsProject(null); }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-blue-600" />
              Proje Ayarları
            </DialogTitle>
          </DialogHeader>
          {settingsProject && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const size = parseInt(settingsMaxFileSize);
                if (!size || size < 1 || size > 200) {
                  toast.error('Dosya boyutu 1 ile 200 MB arası olmalıdır');
                  return;
                }
                setSettingsSaving(true);
                try {
                  await axios.patch(`${API}/projects/${settingsProject.id}`, { max_file_size_mb: size });
                  toast.success('Proje ayarları güncellendi');
                  await fetchProjects();
                  setShowSettingsDialog(false);
                  setSettingsProject(null);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Güncelleme başarısız');
                } finally {
                  setSettingsSaving(false);
                }
              }}
              className="space-y-4"
            >
              <div>
                <Label className="text-sm font-semibold">{settingsProject.name}</Label>
                <p className="text-xs text-gray-500 mt-1">Bu projeye ait özel ayarlar</p>
              </div>

              <div>
                <Label htmlFor="settings-max-file-size" className="text-sm font-semibold">
                  Maks. Dosya Boyutu
                </Label>
                <p className="text-xs text-gray-500 mt-1 mb-2">
                  Yüklenebilecek dosyaların maksimum boyutu (1-200 MB)
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="settings-max-file-size"
                    type="number"
                    min="1"
                    max="200"
                    value={settingsMaxFileSize}
                    onChange={(e) => setSettingsMaxFileSize(e.target.value)}
                    className="flex-1"
                    data-testid="settings-max-file-size-input"
                  />
                  <span className="text-sm text-gray-600 font-medium">MB</span>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Şu anki limit: <span className="font-semibold">{settingsProject.max_file_size_mb || 50} MB</span>
                </p>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>Not:</strong> Bu ayar sadece bu proje için geçerlidir. Varsayılan limit 50 MB'dir.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowSettingsDialog(false); setSettingsProject(null); }}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={settingsSaving}
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                  data-testid="settings-save-btn"
                >
                  {settingsSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, gradient }) {
  return (
    <div className="bg-white rounded-lg p-2.5 border border-gray-100 shadow-sm">
      <div className={`text-xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>{value}</div>
      <div className="text-[11px] text-gray-600 font-medium leading-tight">{label}</div>
    </div>
  );
}
